// src/utils/relicVault/vaultSelectors.js
//
// Derives the vault browser's per-guild relic lists and rarity sections from
// the catalog plus the player's owned relic ids. Kept out of the components so
// the grouping/filtering logic is easy to test. Ported from the designer's
// handoff (lib/vaultSelectors.ts).

import { RELIC_CATALOG } from 'config/relicVault/relics';
import { GUILD_DEFS, TIERS } from 'config/relicVault/guilds';

export const ALL_GUILDS_ID = 'all';

/** Owned-state filter pills in the vault toolbar. */
export const OWNED_FILTERS = [
  { label: 'All', value: 'all' },
  { label: 'Owned', value: 'owned' },
  { label: 'Missing', value: 'missing' },
];

const relicsByGuild = new Map(
  GUILD_DEFS.map(guild => [
    guild.id,
    RELIC_CATALOG.filter(r => r.guild === guild.id),
  ]),
);

/** Relics for a guild (or every guild), each tagged with `owned`. */
export function relicsForGuild(guildId, ownedRelicIds) {
  const ownedSet = new Set(ownedRelicIds);
  const pool =
    guildId === ALL_GUILDS_ID ? RELIC_CATALOG : relicsByGuild.get(guildId) ?? [];
  return pool.map(r => ({ ...r, owned: ownedSet.has(r.id) }));
}

export function passesFilters(relic, query, ownedFilter) {
  if (query && !relic.name.toLowerCase().includes(query.toLowerCase())) return false;
  if (ownedFilter === 'owned' && !relic.owned) return false;
  if (ownedFilter === 'missing' && relic.owned) return false;
  return true;
}

/**
 * One entry per rarity tier for the selected guild. The mythic tier renders as
 * a 1-2 card "feature row" instead of a grid whenever a specific guild (not
 * "All Relics") is selected, matching the design's special mythic treatment.
 *
 * Owned/total counts ignore the search and owned filters, so the section
 * header always reflects real progress.
 */
export function buildRaritySections(currentRelics, selectedGuildId, query, ownedFilter) {
  return TIERS.map(tier => {
    const tierAll = currentRelics.filter(r => r.rarity === tier.key);
    return {
      key: tier.key,
      label: tier.label,
      color: tier.color,
      owned: tierAll.filter(r => r.owned).length,
      total: tierAll.length,
      isMythicFeature: tier.key === 'mythic' && selectedGuildId !== ALL_GUILDS_ID,
      items: tierAll.filter(r => passesFilters(r, query, ownedFilter)),
    };
  });
}

/** Owned / total / percent for the vault header progress bar and rarity chips. */
export function summarizeProgress(currentRelics) {
  const ownedTotal = currentRelics.filter(r => r.owned).length;
  const total = currentRelics.length;
  return {
    ownedTotal,
    total,
    pct: total ? Math.round((ownedTotal / total) * 100) : 0,
    tiers: TIERS.map(t => {
      const items = currentRelics.filter(r => r.rarity === t.key);
      return {
        key: t.key,
        label: t.label,
        color: t.color,
        owned: items.filter(r => r.owned).length,
        total: items.length,
      };
    }),
  };
}
